import type { PagePerformanceReport, PageVisibilityReport } from "~/api/Api";
import { Icon } from "~/assets/icons/Icon";

import MetricBar from "./MetricBar";
import styles from "./PageVisibilityPerformanceBars.module.scss";
import {
  bandFromValue,
  performanceFill,
  performanceScoreLabel,
  visibilitySegmentLabel,
} from "./bands";

interface Props {
  visibilityReport: PageVisibilityReport | null | undefined;
  performanceReports: PagePerformanceReport[] | null | undefined;
  /** Which platform to surface for performance. Defaults to "desktop". */
  platform?: "desktop" | "mobile";
  tooltipId: string;
  /** When true, the AI Visibility Engine is serving prerendered content, so
   * raw visibility/performance bars are irrelevant — show a reassuring icon
   * instead. See isEngineHealthy in ./engineHealth. */
  engineHealthy?: boolean;
  /** Pages table compact mode — when true, the cell is narrow and we render
   * minimal content. In expanded mode the cell has full width, so we can
   * show the icon alongside the bars for engine-healthy rows. */
  isCompactView?: boolean;
}

function formatMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;
}

function visibilityTooltip(report: PageVisibilityReport): string {
  const verdict = visibilitySegmentLabel(report.segment);
  // Cap at 100 — backend can report >1 when raw HTML carries more text than
  // the rendered DOM (e.g. JS strips boilerplate).
  const ratio = Math.min(100, Math.round(report.text_ratio * 100));

  return `Visibility: ${verdict} (${ratio}% text visible without JS)`;
}

function performanceTooltip(report: PagePerformanceReport): string {
  const verdict = performanceScoreLabel(report.score);

  return `Performance: ${verdict} — TTFB ${formatMs(report.absolute_ttfb)}, page load ${formatMs(report.page_load)}`;
}

export default function PageVisibilityPerformanceBars({
  visibilityReport,
  performanceReports,
  platform = "desktop",
  tooltipId,
  engineHealthy = false,
  isCompactView = false,
}: Props) {
  const perf = performanceReports?.find((r) => r.platform === platform) ?? null;

  // text_ratio can exceed 1.0 when raw HTML carries more text than the rendered
  // DOM (e.g. JS strips boilerplate) — cap at 1 so the bar reads as "complete".
  const visValue = visibilityReport
    ? Math.min(1, visibilityReport.text_ratio)
    : null;
  const visBand = visValue !== null ? bandFromValue(visValue) : null;

  const perfValue = perf ? performanceFill(perf.score) : null;
  const perfBand = perfValue !== null ? bandFromValue(perfValue) : null;

  const engineOkIcon = (
    <span
      className={styles.engineOkIcon}
      data-tooltip-id={tooltipId}
      data-tooltip-content="AI Visibility Engine is serving prerendered content — bots and LLMs see the full page."
    >
      <Icon iconName="SiteSpeedIcon" width="1.25rem" height="1.25rem" />
    </span>
  );

  // Compact view with engine healthy: just the icon — minimal footprint.
  if (engineHealthy && isCompactView) {
    return <span className={styles.engineOk}>{engineOkIcon}</span>;
  }

  // No data at all — show a "pending" clock icon (regardless of engine state).
  if (!visibilityReport && !perf) {
    return (
      <span
        className={styles.pending}
        data-tooltip-id={tooltipId}
        data-tooltip-content="Visibility and performance data is being processed — check back shortly."
      >
        <Icon iconName="ClockIcon" width="1.25rem" height="1.25rem" />
      </span>
    );
  }

  const barsContent = (
    <div className={styles.bars}>
      <div className={styles.barRow}>
        <span className={styles.barLabel}>Vis</span>
        <span className={styles.barTrack}>
          <MetricBar
            value={visValue}
            band={visBand}
            data-tooltip-id={tooltipId}
            data-tooltip-content={
              visibilityReport
                ? visibilityTooltip(visibilityReport)
                : "Visibility: not analyzed yet"
            }
          />
        </span>
      </div>
      <div className={styles.barRow}>
        <span className={styles.barLabel}>Perf</span>
        <span className={styles.barTrack}>
          <MetricBar
            value={perfValue}
            band={perfBand}
            data-tooltip-id={tooltipId}
            data-tooltip-content={
              perf ? performanceTooltip(perf) : "Performance: not analyzed yet"
            }
          />
        </span>
      </div>
    </div>
  );

  // Expanded view with engine healthy: icon on the left, raw bars on the right
  // so the user gets reassurance AND the underlying crawl numbers.
  if (engineHealthy) {
    return (
      <div className={styles.engineOkWithBars}>
        {engineOkIcon}
        {barsContent}
      </div>
    );
  }

  return barsContent;
}
